import { z } from "zod"

/**
 * The live portal preview in the firm app's portal studio.
 *
 * The studio frames the portal's own demo (`/demo/firms` on the firm's portal
 * host) and loads it once. The first URL carries the state at load; every
 * change after that is a message, so the frame never reloads while a firm
 * edits. Both apps import this module: the app writes the URL and the
 * messages with it, and the portal demo reads them with it.
 *
 * The handshake: the demo sends `ready` to its parent when it can listen, and
 * the app answers with the current `state`, then sends `state` again on every
 * change. Each side accepts a message only from the other window and from
 * the other side's origin. The app knows the portal origin from its settings;
 * the demo learns the app origin from the `parent` query parameter.
 */

/** Tags every preview message, so neither side reads another script's post. */
export const PORTAL_PREVIEW_SOURCE = "ledgerbox:portal-preview" as const

const HEX = /^#[0-9a-fA-F]{6}$/
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const webUrl = z
  .string()
  .max(2048)
  .refine((value) => {
    try {
      const url = new URL(value)
      return url.protocol === "https:" || url.protocol === "http:"
    } catch {
      return false
    }
  })

/**
 * What the preview draws of the firm's settings. Copy fields hold "" for
 * "use the portal's own words", as the studio's fields do.
 */
export const PortalPreviewBrandSchema = z.object({
  displayName: z.string().trim().min(1).max(120),
  accentColor: z.string().regex(HEX),
  logoUrl: webUrl.nullable(),
  showNameWithLogo: z.boolean(),
  homeGreeting: z.string().max(200),
  homeMessage: z.string().max(2000),
  supportName: z.string().max(120),
  supportEmail: z.union([z.literal(""), z.string().max(254).regex(EMAIL)]),
  supportPhone: z.string().max(60),
  /* The firm's side of an accounting access item, from Connections. The
     Request item page shows them; "" leaves a row out. */
  accountantEmail: z.union([z.literal(""), z.string().max(254).regex(EMAIL)]),
  quickbooksFirmId: z.string().max(120),
})

export type PortalPreviewBrand = z.infer<typeof PortalPreviewBrandSchema>

/** Every field the preview can draw, in one list for the contract tests. */
export const portalPreviewBrandFields = Object.keys(
  PortalPreviewBrandSchema.shape
) as (keyof PortalPreviewBrand)[]

/** The portal screens the preview can show: the client home, the sign-in
    page, what a client with only the link sees (the open items, and a way
    to sign in for the rest), one request item (the accounting access
    item, open), and the client's History of every file sent. */
export const PortalPreviewScreenSchema = z.enum([
  "home",
  "signin",
  "link",
  "item",
  "history",
])
export type PortalPreviewScreen = z.infer<typeof PortalPreviewScreenSchema>

export const PortalPreviewStateSchema = z.object({
  brand: PortalPreviewBrandSchema,
  screen: PortalPreviewScreenSchema,
})

export type PortalPreviewState = z.infer<typeof PortalPreviewStateSchema>

export const PortalPreviewMessageSchema = z.discriminatedUnion("type", [
  /** App to demo: draw this. */
  z.object({
    source: z.literal(PORTAL_PREVIEW_SOURCE),
    type: z.literal("state"),
    state: PortalPreviewStateSchema,
  }),
  /** Demo to app: I listen now; send the state. */
  z.object({
    source: z.literal(PORTAL_PREVIEW_SOURCE),
    type: z.literal("ready"),
  }),
])

export type PortalPreviewMessage = z.infer<typeof PortalPreviewMessageSchema>

export const portalPreviewStateMessage = (
  state: PortalPreviewState
): PortalPreviewMessage => ({
  source: PORTAL_PREVIEW_SOURCE,
  type: "state",
  state,
})

export const portalPreviewReadyMessage = (): PortalPreviewMessage => ({
  source: PORTAL_PREVIEW_SOURCE,
  type: "ready",
})

/** A posted message as a preview message, or null for anything else. */
export const readPortalPreviewMessage = (
  data: unknown
): PortalPreviewMessage | null => {
  const parsed = PortalPreviewMessageSchema.safeParse(data)
  return parsed.success ? parsed.data : null
}

/**
 * Brand fields that only ever arrive by message from the firm app, never in
 * the query. /demo answers on every portal host, so a link with an
 * accountant email in it could show a firm's client a real-looking "invite
 * this address to your books" step with an attacker's address.
 */
export const portalPreviewMessageOnlyFields = [
  "accountantEmail",
  "quickbooksFirmId",
] as const satisfies readonly (keyof PortalPreviewBrand)[]

type PortalPreviewQueryField = Exclude<
  keyof PortalPreviewBrand,
  (typeof portalPreviewMessageOnlyFields)[number]
>

/** The brand fields a preview query carries. */
export const portalPreviewQueryFields = portalPreviewBrandFields.filter(
  (field): field is PortalPreviewQueryField =>
    !(portalPreviewMessageOnlyFields as readonly string[]).includes(field)
)

/**
 * The query parameter for each brand field a query may carry. The names are
 * the ones the public demo has always read, so an old link keeps its brand.
 */
export const PORTAL_PREVIEW_QUERY = {
  displayName: "name",
  accentColor: "accent",
  logoUrl: "logo",
  showNameWithLogo: "showName",
  homeGreeting: "greeting",
  homeMessage: "message",
  supportName: "supportName",
  supportEmail: "support",
  supportPhone: "phone",
} as const satisfies Record<PortalPreviewQueryField, string>

/** The screen, and the app origin the demo may take messages from. */
export const PORTAL_PREVIEW_SCREEN_QUERY = "screen"
export const PORTAL_PREVIEW_PARENT_QUERY = "parent"

/** The origin of an http(s) URL, or null. */
export const webOrigin = (value: string | null | undefined): string | null => {
  if (!value) return null
  try {
    const url = new URL(value)
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.origin
      : null
  } catch {
    return null
  }
}

/**
 * The query the preview frame loads with: the whole state, and the origin
 * of the app that will post to it. Empty copy is left out.
 */
export const portalPreviewSearch = (
  state: PortalPreviewState,
  parentOrigin: string
): URLSearchParams => {
  const search = new URLSearchParams()
  const { brand } = state
  for (const field of portalPreviewQueryFields) {
    const name = PORTAL_PREVIEW_QUERY[field]
    const value = brand[field]
    if (typeof value === "boolean") {
      if (!value) search.set(name, "0")
    } else if (value?.trim()) search.set(name, value.trim())
  }
  if (state.screen !== "home")
    search.set(PORTAL_PREVIEW_SCREEN_QUERY, state.screen)
  const parent = webOrigin(parentOrigin)
  if (parent) search.set(PORTAL_PREVIEW_PARENT_QUERY, parent)
  return search
}

export type PortalPreviewSearch = {
  /** Only the fields the query holds and that pass their check. */
  brand: Partial<PortalPreviewBrand>
  screen: PortalPreviewScreen
  /** The app origin that may post state, or null when nobody may. */
  parentOrigin: string | null
}

/**
 * Reads a preview query field by field. A field that fails its check is
 * dropped, not the whole brand: this is also the public demo's link, and a
 * bad logo must not cost a prospect their name and color.
 */
export const readPortalPreviewSearch = (
  search: URLSearchParams
): PortalPreviewSearch => {
  const brand: Partial<PortalPreviewBrand> = {}
  const shape = PortalPreviewBrandSchema.shape
  for (const field of portalPreviewQueryFields) {
    const raw = search.get(PORTAL_PREVIEW_QUERY[field])
    if (raw === null) continue
    const value: unknown =
      field === "showNameWithLogo" ? raw !== "0" : clip(field, raw.trim())
    const parsed = shape[field].safeParse(value)
    if (parsed.success && parsed.data !== "")
      Object.assign(brand, { [field]: parsed.data })
  }
  const screen = PortalPreviewScreenSchema.safeParse(
    search.get(PORTAL_PREVIEW_SCREEN_QUERY)
  )
  return {
    brand,
    screen: screen.success ? screen.data : "home",
    parentOrigin: webOrigin(search.get(PORTAL_PREVIEW_PARENT_QUERY)),
  }
}

/* Free text longer than its setting allows is cut, not dropped, as the
   demo always did. */
const TEXT_MAX: Partial<Record<keyof PortalPreviewBrand, number>> = {
  displayName: 120,
  homeGreeting: 200,
  homeMessage: 2000,
  supportName: 120,
  supportPhone: 60,
  quickbooksFirmId: 120,
}

const clip = (field: keyof PortalPreviewBrand, value: string): string => {
  const max = TEXT_MAX[field]
  return max === undefined ? value : value.slice(0, max)
}
